/**
 * PageSettingsPanel Component
 *
 * Read-only "Page Drivers" view of the Page side panel. A "Page Settings" selector switches between the
 * page's driver set for each configured dataset (Galaxy, HCTR) and, when a denominator label is set, the
 * drivers used for the denominator (e.g. "% Gross Margin").
 */

import './PageSettingsPanel.scss';

import { DenominatorLabels } from '@constants/page';
import { usePageContext } from '@context/PageContext';
import { KPIDriversDisplay } from '@deckManagement/components/Common/KPIDriversDisplay';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { useMemo } from 'react';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { PAGE_SETTINGS_DENOMINATOR_VIEW, PageSettingsPanelProps, PageSettingsView } from 'types/PageSidePanel.types';

/**
 * Only Galaxy supports denominator drivers.
 */
const DENOMINATOR_DATASET = DatasetSource.GALAXY;

const PageSettingsPanel = ({ selectedView, onViewChange }: PageSettingsPanelProps) => {
  const { currentPageDriverOptions, modifiedPageProperties, configuredDatasets } = usePageContext();
  const { denominator_label: denominatorLabel } = modifiedPageProperties || {};

  /**
   * Selector options: each configured dataset (in DatasetSource order, falling back to Galaxy when the page
   * has no driver sets), followed by the denominator when the page has a denominator label.
   */
  const viewOptions = useMemo(() => {
    const datasets = Object.values(DatasetSource).filter((ds) => configuredDatasets.includes(ds));
    const options: Array<{ value: PageSettingsView; label: string }> = (
      datasets.length ? datasets : [DatasetSource.GALAXY]
    ).map((ds) => ({ value: ds, label: resolveDatasetSourceLabel(ds) }));
    if (denominatorLabel) {
      options.push({ value: PAGE_SETTINGS_DENOMINATOR_VIEW, label: DenominatorLabels[denominatorLabel] });
    }
    return options;
  }, [configuredDatasets, denominatorLabel]);

  // Fall back to the first option when the selection is unset or no longer offered (e.g. the denominator
  // label was removed, or a different page loaded).
  const activeView = viewOptions.some((option) => option.value === selectedView) ? selectedView! : viewOptions[0].value;
  const isDenominatorView = activeView === PAGE_SETTINGS_DENOMINATOR_VIEW;
  const activeDataset = isDenominatorView ? DENOMINATOR_DATASET : activeView;

  /**
   * Readonly driver options for the active dataset, read directly from the dataset-keyed context map.
   * The denominator view has no separate `Page` drivers yet (see Schema:
   * https://tiny.amazon.com/3xhnemqx), so it reuses the Galaxy entry — which also tracks side-panel
   * edits.
   */
  const driverOptions = useMemo<DriverDisplayOptionMap | null>(
    () => currentPageDriverOptions?.[activeDataset] ?? null,
    [activeDataset, currentPageDriverOptions],
  );

  /**
   * Handles a Page Settings selector change.
   * @param event - The Select change event carrying the new view
   */
  const handleViewChange = (event: SelectChangeEvent<PageSettingsView>) => {
    onViewChange(event.target.value as PageSettingsView);
  };

  return (
    <Stack className='page-settings-panel'>
      <FormControl className='page-settings-panel__select' size='small' fullWidth>
        <InputLabel id='page-settings-select-label'>Page Settings</InputLabel>
        <Select
          labelId='page-settings-select-label'
          label='Page Settings'
          value={activeView}
          onChange={handleViewChange}
        >
          {viewOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <List className='segments page-settings-panel__source'>
        <ListItem disablePadding>
          <ListItemText
            primary='Source'
            secondary={resolveDatasetSourceLabel(activeDataset)}
            slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
          />
        </ListItem>
      </List>
      {driverOptions && (
        <KPIDriversDisplay
          className='page-settings-panel__drivers'
          datasetSource={activeDataset}
          driverOptions={driverOptions}
          renderMode='readonly'
        />
      )}
    </Stack>
  );
};

export default PageSettingsPanel;
